import { CKEditor } from "@ckeditor/ckeditor5-react";
import { useUploadFileMutation } from "@generated/graphql";
import { Box } from "@mui/material";
import {
  ClassicEditor,
  Alignment,
  Autoformat,
  Autosave,
  BlockQuote,
  Bold,
  Essentials,
  GeneralHtmlSupport,
  Heading,
  HorizontalLine,
  Italic,
  Paragraph,
  PasteFromOffice,
  Strikethrough,
  TextTransformation,
  Underline,
  EditorConfig,
  CodeBlock,
  Image,
  ImageToolbar,
  ImageUpload,
  Editor,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";
import { ToastContext } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import { useContext, useEffect, useRef, useState } from "react";
import UploadAdapter from "./UploadAdapter";

const LICENSE_KEY = "GPL";

export interface TextEditorBasicProps {
  placeholder?: string;
  disabled?: boolean;
}

interface TextEditorProps extends TextEditorBasicProps {
  value: string;
  onChange: (data: string) => void;
  isValid?: boolean;
}

const TextEditor = ({ value, onChange, isValid = true, placeholder, disabled }: TextEditorProps) => {
  const editorContainerRef = useRef(null);
  const editorRef = useRef(null);
  const { addToast } = useContext(ToastContext);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [uploadFile] = useUploadFileMutation();

  const onError = (text: string) => {
    addToast({ text, type: "error" });
  };

  const handleUploadFile = async (file: File) => {
    return uploadFile({ variables: { file } })
      .then(({ data }) => ({ default: data?.uploadFile.url || "" }))
      .catch((e) => {
        parseApolloError(e, { addToast });
        return { default: "" };
      });
  };

  useEffect(() => {
    setIsLayoutReady(true);

    return () => setIsLayoutReady(false);
  }, []);

  function UploadAdapterPlugin(editor: Editor) {
    // eslint-disable-next-line no-param-reassign
    editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
      return new UploadAdapter(loader, handleUploadFile, onError);
    };
  }

  const editorConfig: EditorConfig = {
    toolbar: {
      items: [
        "heading",
        "|",
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "|",
        "horizontalLine",
        "blockQuote",
        "codeBlock",
        "|",
        "alignment",
        "imageUpload",
      ],
      shouldNotGroupWhenFull: false,
    },
    simpleUpload: {
      uploadUrl: "/upload-image",
      // headers: {
      //   "X-CSRF-TOKEN": "your-csrf-token-here",
      // },
    },
    plugins: [
      Alignment,
      Autoformat,
      Autosave,
      BlockQuote,
      Bold,
      Essentials,
      GeneralHtmlSupport,
      Heading,
      HorizontalLine,
      Italic,
      Paragraph,
      PasteFromOffice,
      Strikethrough,
      TextTransformation,
      Underline,
      CodeBlock,
      Image,
      ImageToolbar,
      ImageUpload,
    ],
    extraPlugins: [UploadAdapterPlugin],
    codeBlock: {
      languages: [
        { language: "plaintext", label: "Plain text" },
        { language: "c", label: "C" },
        { language: "cs", label: "C#" },
        { language: "cpp", label: "C++" },
        { language: "css", label: "CSS" },
        { language: "diff", label: "Diff" },
        { language: "html", label: "HTML" },
        { language: "java", label: "Java" },
        { language: "javascript", label: "JavaScript" },
        { language: "php", label: "PHP" },
        { language: "python", label: "Python" },
        { language: "ruby", label: "Ruby" },
        { language: "typescript", label: "TypeScript" },
        { language: "xml", label: "XML" },
        { language: "sql", label: "SQL" },
        { language: "go", label: "Go" },
      ],
    },
    heading: {
      options: [
        {
          model: "paragraph",
          title: "Paragraph",
          class: "ck-heading_paragraph",
        },
        {
          model: "heading1",
          view: "h1",
          title: "Heading 1",
          class: "ck-heading_heading1",
        },
        {
          model: "heading2",
          view: "h2",
          title: "Heading 2",
          class: "ck-heading_heading2",
        },
        {
          model: "heading3",
          view: "h3",
          title: "Heading 3",
          class: "ck-heading_heading3",
        },
        {
          model: "heading4",
          view: "h4",
          title: "Heading 4",
          class: "ck-heading_heading4",
        },
        {
          model: "heading5",
          view: "h5",
          title: "Heading 5",
          class: "ck-heading_heading5",
        },
        {
          model: "heading6",
          view: "h6",
          title: "Heading 6",
          class: "ck-heading_heading6",
        },
      ],
    },
    fontFamily: {
      options: ["default"],
    },
    fontSize: {
      options: [10, 12, 14, "default", 18, 20, 22],
      supportAllValues: true,
    },
    htmlSupport: {
      allow: [
        {
          name: /^.*$/,
          styles: true,
          attributes: true,
          classes: true,
        },
      ],
    },
    licenseKey: LICENSE_KEY,
    initialData: value,
    placeholder,
  };

  return (
    <div className='main-container'>
      <Box
        sx={{
          lineHeight: 1.6,
          wordBreak: "break-word",
          "&.editor-container .ck.ck-editor__main>.ck-editor__editable": {
            minHeight: 300,
            borderColor: !isValid ? "error.main" : "",
          },
        }}
        className='editor-container editor-container_classic-editor'
        ref={editorContainerRef}>
        <div className='editor-container__editor'>
          <div ref={editorRef}>
            {isLayoutReady && (
              <CKEditor
                editor={ClassicEditor}
                config={editorConfig}
                data={value}
                disabled={disabled}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  onChange(data);
                }}
              />
            )}
          </div>
        </div>
      </Box>
    </div>
  );
};

export default TextEditor;
